// src/utils/sampleDocument.ts

/**
 * Sample document utilities for onboarding new users
 */

const SAMPLE_PDF_URL = '/sample-reports/Full_body_tests.pdf';
const SAMPLE_PDF_FILENAME = 'Full_body_tests.pdf';

/**
 * Downloads the sample PDF to the user's device
 */
export const downloadSamplePDF = async (): Promise<void> => {
  try {
    // Fetch the PDF from public folder
    const response = await fetch(SAMPLE_PDF_URL);

    if (!response.ok) {
      throw new Error('Failed to fetch sample PDF');
    }

    const blob = await response.blob();

    // Create a temporary download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = SAMPLE_PDF_FILENAME;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('✅ Sample PDF downloaded to device');
  } catch (error) {
    console.error('❌ Failed to download sample PDF:', error);
    throw error;
  }
};

/**
 * Fetches the sample PDF as a File object for uploading
 */
export const fetchSamplePDFAsFile = async (): Promise<File> => {
  try {
    // Fetch the PDF from public folder
    const response = await fetch(SAMPLE_PDF_URL);

    if (!response.ok) {
      throw new Error('Failed to fetch sample PDF');
    }

    const blob = await response.blob();

    // Convert blob to File object
    const file = new File([blob], SAMPLE_PDF_FILENAME, {
      type: 'application/pdf',
      lastModified: Date.now(),
    });

    console.log('✅ Sample PDF fetched as File object:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    return file;
  } catch (error) {
    console.error('❌ Failed to fetch sample PDF as File:', error);
    throw error;
  }
};

/**
 * Loads the sample document: downloads it to device AND returns it as a File for processing
 */
export const loadSampleDocument = async (): Promise<File> => {
  try {
    // Download to user's device
    await downloadSamplePDF();

    // Also return as File object for uploading/processing
    const file = await fetchSamplePDFAsFile();

    return file;
  } catch (error) {
    console.error('❌ Failed to load sample document:', error);
    throw error;
  }
};
